/**
 * 孩子端「今日打卡」页里的**语文听写入口**（迁移 0020）。
 *
 * ## 用户原话
 *
 * > "孩子端要有听写入口 —— 这样就不用大人陪着。"
 *
 * ## 为什么是"卡片"而不是底部导航里再加一项
 *
 * 底部 tab 在 320px 的机型上已经是 5 项均分（见 `navConfig` / `AppShell` 里那段
 * 关于"折行会把 tab 栏顶高、压住内容"的注释）。再加一项会把「今日打卡」这类
 * 四字标签挤到边缘。而听写对孩子的意义是"**今天要做的一件事**"，
 * 它天然属于今日清单这一屏 —— 所以做成顶部的卡片，与"今日进度"挨着。
 *
 * ## 没有任务时**整块不渲染**
 *
 * 家长还没布置批量任务时，给孩子看一个空盒子只会让他点进去发现什么都没有。
 * 所以：`assignments` 为空 / 没拿到 / 请求失败 → 返回 `null`（静默不出现）。
 * 这与"界面绝不静默兜底"的原则不冲突：这里是**入口的存在性**，不是数据展示 ——
 * 真正的数据展示在 `/student/dictation` 那一页（那里三态齐全）。
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as assignmentApi from '../../lib/api/dictationAssignments';

export interface StudentDictationEntryProps {
  /** `students.id` */
  studentId: string;
  /** 儿童模式（字更大、留白更多）。 */
  kid: boolean;
}

export function StudentDictationEntry({ studentId, kid }: StudentDictationEntryProps) {
  const query = useQuery({
    queryKey: ['dictation-assignments', studentId, 'list'] as const,
    queryFn: () => assignmentApi.listAssignments(studentId, { limit: 20 }),
    enabled: studentId !== '',
    /** 30 秒内不重复取：这一块只是入口，没必要每次切回首页都请求一遍。 */
    staleTime: 30_000,
  });

  const assignments = query.data?.assignments ?? null;
  if (assignments === null) return null;

  const usable = assignments.filter((item) => item.status !== 'finished');
  if (usable.length === 0) return null;

  /** 最该做的那一个：进行中优先，其次看剩下多少词。 */
  const active = usable.filter((item) => item.status === 'active');
  const first = active[0] ?? usable[0];
  if (first === undefined) return null;

  const remaining = first.progress.remainingWords;
  const allDone = first.progress.allDone === true;
  const paused = first.status === 'paused';

  return (
    <Link
      to="/student/dictation"
      className={`block rounded-2xl border border-brand-200 bg-white hover:bg-brand-50 ${
        kid ? 'px-4 py-4' : 'px-4 py-3'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className={`font-semibold text-slate-900 ${kid ? 'text-lg' : 'text-sm'}`}>
            ✍️ 语文听写
            {usable.length > 1 && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                还有 {usable.length} 个任务
              </span>
            )}
          </p>
          <p className={`mt-0.5 truncate text-slate-500 ${kid ? 'text-base' : 'text-xs'}`}>
            {first.title} ·{' '}
            {allDone
              ? '这个任务已经全部听完了 🎉'
              : paused
                ? '家长暂停了这个任务'
                : typeof remaining === 'number'
                  ? `还剩 ${remaining} 个词`
                  : '进度没拿到'}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-xl bg-brand-600 font-semibold text-white ${
            kid ? 'px-4 py-3 text-lg' : 'px-3 py-2 text-sm'
          }`}
        >
          {allDone ? '看看' : '去听写'}
        </span>
      </div>
    </Link>
  );
}
